import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" }
};

const BLANK = { courseId: "", semesterId: "", examDate: "", startTime: "", endTime: "", venue: "", invigilator: "" };

class ExamSchedule extends Component {
  state = {
    schedules: [],
    semesters: [],
    courses: [],
    selectedSemester: "",
    loading: false,
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchMeta(); }

  fetchMeta = async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        api.get("/semesters?limit=100"),
        api.get("/courses?limit=100")
      ]);
      const sd = sRes.data.data || sRes.data;
      const cd = cRes.data.data || cRes.data;
      const semesters = Array.isArray(sd) ? sd : sd.data || [];
      const current = semesters.find(s => s.isCurrent);
      this.setState({
        semesters,
        courses: Array.isArray(cd) ? cd : cd.data || [],
        selectedSemester: current ? current.id : ""
      }, () => { if (this.state.selectedSemester) this.fetchSchedules(); });
    } catch (e) { /* silent */ }
  };

  fetchSchedules = async () => {
    const { selectedSemester } = this.state;
    if (!selectedSemester) return;
    this.setState({ loading: true });
    try {
      const res = await api.get("/exam-schedules", { params: { semesterId: selectedSemester, limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ schedules: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.courseId || !form.semesterId || !form.examDate) {
      this.setState({ error: "Course, semester, and exam date are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editing) await api.put(`/exam-schedules/${editing}`, form);
      else await api.post("/exam-schedules", form);
      this.setState({ open: false, saving: false });
      this.fetchSchedules();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Delete this exam slot?")) return;
    try {
      await api.delete(`/exam-schedules/${id}`);
      this.fetchSchedules();
    } catch (e) { /* silent */ }
  };

  openEdit = (s) => {
    this.setState({
      open: true, editing: s.id, error: null,
      form: {
        courseId: s.course?.id || s.courseId || "",
        semesterId: s.semester?.id || s.semesterId || "",
        examDate: s.examDate || "",
        startTime: s.startTime || "",
        endTime: s.endTime || "",
        venue: s.venue || "",
        invigilator: s.invigilator || ""
      }
    });
  };

  render() {
    const { classes } = this.props;
    const { schedules, semesters, courses, selectedSemester, loading, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "code", header: "Code", accessorFn: s => s.course?.code || "—" },
      { id: "course", header: "Course", accessorFn: s => s.course?.name || "—" },
      { id: "date", header: "Date", accessorFn: s => s.examDate || "—" },
      { id: "time", header: "Time", accessorFn: s => (s.startTime ? `${s.startTime} – ${s.endTime || ""}` : "—") },
      { id: "venue", header: "Venue", accessorFn: s => s.venue || "—" },
      { id: "invigilator", header: "Invigilator", accessorFn: s => s.invigilator || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>Edit</Button>
            <Button size="sm" color="danger" simple onClick={() => this.handleDelete(info.row.original.id)}>Del</Button>
          </div>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          <FormControl style={{ minWidth: "240px", marginBottom: "16px" }}>
            <InputLabel>Semester</InputLabel>
            <Select value={selectedSemester} onChange={e => this.setState({ selectedSemester: e.target.value }, this.fetchSchedules)}>
              <MenuItem value="">— Select Semester —</MenuItem>
              {semesters.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DataTable
            title="Exam Schedule"
            subtitle="Plan and publish examination timetables"
            actions={
              <Button size="sm" color="primary" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK, semesterId: selectedSemester }, error: null })}>
                + Add Exam Slot
              </Button>
            }
            columns={columns}
            data={schedules}
            isLoading={loading}
            emptyState={selectedSemester ? "No exam slots scheduled for this semester." : "Select a semester to view the schedule."}
          />
        </GridItem>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Exam Slot" : "Add Exam Slot"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Course *</InputLabel>
              <Select value={form.courseId} onChange={this.handleChange("courseId")}>
                <MenuItem value="">— Select Course —</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Semester *</InputLabel>
              <Select value={form.semesterId} onChange={this.handleChange("semesterId")}>
                <MenuItem value="">— Select Semester —</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomDatePicker labelText="Exam Date *" id="examDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.examDate, onChange: this.handleChange("examDate") }} />
            <GridContainer>
              <GridItem xs={6}>
                <CustomDatePicker dateFormat={false} timeFormat="HH:mm" labelText="Start Time" id="startTime" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.startTime, onChange: this.handleChange("startTime") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomDatePicker dateFormat={false} timeFormat="HH:mm" labelText="End Time" id="endTime" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.endTime, onChange: this.handleChange("endTime") }} />
              </GridItem>
            </GridContainer>
            <CustomInput labelText="Venue / Room" id="venue" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.venue, onChange: this.handleChange("venue") }} />
            <CustomInput labelText="Invigilator" id="invigilator" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.invigilator, onChange: this.handleChange("invigilator") }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

ExamSchedule.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ExamSchedule));
